import inlineDictationEditor from './dictation/inline-editor.js';
import { formatMessageDateTime } from "./messageTime.js?v=45.0.0"
import {
    escapePlaintextHtml,
    sanitizeRichHtml,
} from "./chatFrontendGuards.js?v=1.0.2"

export default {
    mixins: [inlineDictationEditor],
    props: {
        message: Object,
        index: Number,
        isLastUser: {
            type: Boolean,
            default: false
        },
        replying: {
            type: Boolean,
            default: false
        },
        collaborative: {
            type: Boolean,
            default: false
        },
        chatUuid: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            editing: false,
            editText: this.message?.text || "",
            showConversationHistory: false,
            activeReactionDetails: null,
        }
    },
    computed: {
        formattedText() {
            const source = String(this.message && this.message.text || '');
            try {
                const html = this.styleCollaborativeMentions(marked.parse(source));
                return sanitizeRichHtml(html, source);
            } catch (error) {
                return escapePlaintextHtml(source);
            }
        },
        messageContextDocuments() {
            return this.normalizeContextDocuments(this.message?.contextDocuments);
        },
        hasContextDocuments() {
            return this.messageContextDocuments.length > 0;
        },
        contextDocumentsLabel() {
            return this.messageContextDocuments.length === 1
                ? "Documento incluido en este mensaje"
                : "Documentos incluidos en este mensaje";
        },
        senderEmail() {
            return String(this.message?.sender_email || '').trim();
        },
        senderDisplayName() {
            return String(this.message?.sender_display_name || '').trim();
        },
        senderPrimaryLabel() {
            if (!this.collaborative) return "";
            const displayName = this.senderDisplayName;
            const email = this.senderEmail;
            const displayNameIsEmail = displayName && displayName.toLowerCase() === email.toLowerCase();
            if (displayName && !displayNameIsEmail && !displayName.includes('@')) {
                return displayName.split(/\s+/).filter(Boolean)[0] || displayName;
            }
            if (email && email.includes('@')) {
                const localPart = email.split('@')[0].split(/[._-]+/).filter(Boolean)[0] || email.split('@')[0];
                return localPart ? localPart.charAt(0).toUpperCase() + localPart.slice(1) : email;
            }
            return this.message?.sender_username || "Participante";
        },
        messageDateTime() {
            if (!this.collaborative) return "";
            return formatMessageDateTime(this.message);
        },
        senderSecondaryEmail() {
            if (!this.collaborative) return "";
            const email = this.senderEmail;
            if (!email) return "";
            return email;
        },
        senderAvatarInitial() {
            if (!this.collaborative) return "";
            const displayName = this.senderDisplayName;
            const email = this.senderEmail;
            const displayNameIsEmail = displayName && email && displayName.toLowerCase() === email.toLowerCase();
            const displayNameIsUuid = displayName && this.isUuidLike(displayName);
            const source = displayName && !displayNameIsEmail && !displayNameIsUuid
                ? displayName
                : (this.senderPrimaryLabel || email || this.message?.sender_username || 'Participante');
            return this.avatarInitial(source, 'P');
        },
        replyPreview() {
            if (!this.collaborative) return null;
            const reply = this.message?.reply_to || this.message?.replyTo;
            if (!reply || typeof reply !== 'object') return null;
            const snippet = String(reply.snippet || '').trim();
            if (!snippet) return null;
            return {
                message_id: String(reply.message_id || '').trim(),
                sender_type: String(reply.sender_type || reply.sender || '').trim(),
                sender_label: String(reply.sender_label || '').trim() || 'Mensaje citado',
                snippet,
                timestamp: String(reply.timestamp || '').trim(),
            };
        },
        canReply() {
            return this.collaborative && !this.editing;
        },
        messageReactions() {
            if (!Array.isArray(this.message?.reactions)) return [];
            return this.message.reactions.filter((item) => item && ['agree', 'disagree'].includes(item.reaction));
        },
        groupedReactions() {
            const grouped = {};
            this.messageReactions.forEach((item) => {
                const key = item && item.reaction ? String(item.reaction) : '';
                if (!key) return;
                if (!grouped[key]) grouped[key] = { reaction: key, count: 0, names: [], reactors: [] };
                grouped[key].count += 1;
                const actor = this.reactionActor(item);
                if (actor.label) {
                    grouped[key].names.push(actor.label);
                    grouped[key].reactors.push(actor);
                }
            });
            return Object.values(grouped);
        },
        activeReactionGroup() {
            if (!this.activeReactionDetails) return null;
            return this.groupedReactions.find((item) => item.reaction === this.activeReactionDetails) || null;
        },
        currentUserReactionMap() {
            const currentEmail = String(window.user_email || '').trim().toLowerCase();
            const currentUsername = String(window.user_name || window.user_id || '').trim().toLowerCase();
            const reactionMap = {};
            this.messageReactions.forEach((item) => {
                const username = String(item?.reactor_username || '').trim().toLowerCase();
                const email = String(item?.reactor_email || '').trim().toLowerCase();
                const displayName = String(item?.reactor_display_name || '').trim().toLowerCase();
                const belongsToCurrentUser = !!(
                    (currentUsername && username && currentUsername === username)
                    || (currentEmail && email && currentEmail === email)
                    || (currentEmail && displayName && currentEmail === displayName)
                );
                if (belongsToCurrentUser && item?.reaction) {
                    reactionMap[item.reaction] = true;
                }
            });
            return reactionMap;
        },
        hasConversationHistory() {
            return Array.isArray(this.message?.conversationHistory) && this.message.conversationHistory.length > 0;
        },
        sortedConversationHistory() {
            if (!this.hasConversationHistory) return [];
            // Sort by editedAt timestamp, most recent first
            return [...this.message.conversationHistory].sort((a, b) => b.editedAt - a.editedAt);
        },
        canEdit() {
            return this.isLastUser && !this.replying && this.message?.human_only !== true && this.messageBelongsToCurrentUser;
        },
        aiQueueStatus() {
            return String(this.message?.ai_queue_status || 'queued');
        },
        isAiQueued() {
            return this.message?.ai_queued === true && ['queued', 'running'].includes(this.aiQueueStatus);
        },
        aiQueueBadgeText() {
            return this.aiQueueStatus === 'running' ? 'Procesando en Lexius' : 'En cola para Lexius';
        },
        aiQueueReason() {
            if (this.aiQueueStatus === 'running') {
                return 'Lexius está procesando esta solicitud.';
            }
            return String(this.message?.ai_queue_reason || 'Lexius terminará la respuesta actual antes de atender esta solicitud.').trim();
        },
        canRunQueuedAi() {
            return this.isAiQueued && this.aiQueueStatus === 'queued' && this.messageBelongsToCurrentUser && !this.replying;
        },
        messageBelongsToCurrentUser() {
            if (!this.collaborative) return true;
            const currentEmail = String(window.user_email || '').trim().toLowerCase();
            const senderEmail = String(this.message?.sender_email || '').trim().toLowerCase();
            if (senderEmail && currentEmail) {
                return senderEmail === currentEmail;
            }
            return false;
        },
        editButtonTitle() {
            if (!this.isLastUser) {
                return "Solo puedes editar tu último mensaje";
            }
            if (!this.messageBelongsToCurrentUser) {
                return "Solo puedes editar tus propios mensajes";
            }
            if (this.replying) {
                return "Espera a que termine la respuesta para editar, o cancela la respuesta para editar este mensaje";
            }
            if (this.message?.human_only === true) {
                return "Los mensajes del grupo no se editan desde el flujo del Abogado Virtual";
            }
            return "Editar este mensaje";
        },
        showButtons() {
            // Show buttons if it's the last user message OR if there's conversation history
            return this.canEdit || this.hasConversationHistory;
        }
    },
    mounted() {
        try {
            document.addEventListener('click', this.handleReactionDetailsOutsideClick);
            document.addEventListener('keydown', this.handleReactionDetailsKeydown);
        } catch (e) { }
    },
    beforeDestroy() {
        try {
            document.removeEventListener('click', this.handleReactionDetailsOutsideClick);
            document.removeEventListener('keydown', this.handleReactionDetailsKeydown);
        } catch (e) { }
    },
    methods: {
        startEdit() {
            if (!this.canEdit) {
                alert(this.editButtonTitle);
                return;
            }
            this.beginInlineDictationEditor();
            this.editing = true;
            this.editText = this.message.text;
        },
        cancelEdit() {
            this.cancelInlineDictation();
            this.dictationRecovery = "";
            this.editing = false;
            this.editText = this.message.text;
        },
        confirmEdit() {
            if (!this.inlineCanConfirm) return;
            this.cancelInlineDictation();
            const trimmed = (this.editText || "").trim();
            if (!trimmed) {
                alert("El mensaje no puede estar vacío.");
                return;
            }
            // Emit event to parent (ChatLayout) to re-run the chat from this point
            this.$emit('editUserMessage', { original: this.message.text, updated: trimmed });
            this.editing = false;
        },
        toggleConversationHistory() {
            this.showConversationHistory = !this.showConversationHistory;
        },
        formatBotResponseText(botResponse) {
            if (!botResponse || !botResponse.text) return "";
            try {
                return sanitizeRichHtml(marked.parse(botResponse.text), botResponse.text);
            } catch (e) {
                return escapePlaintextHtml(botResponse.text);
            }
        },
        formatUserText(userText) {
            try {
                return sanitizeRichHtml(marked.parse(userText), userText);
            } catch (e) {
                return escapePlaintextHtml(userText);
            }
        },
        styleCollaborativeMentions(html) {
            if (!this.collaborative || !html || typeof document === 'undefined') {
                return html;
            }
            try {
                const wrapper = document.createElement('div');
                wrapper.innerHTML = html;
                const skipTags = new Set(['A', 'CODE', 'PRE', 'SCRIPT', 'STYLE', 'TEXTAREA']);
                const textNodes = [];
                const walker = document.createTreeWalker(
                    wrapper,
                    NodeFilter.SHOW_TEXT,
                    {
                        acceptNode(node) {
                            let parent = node.parentElement;
                            while (parent) {
                                if (skipTags.has(parent.tagName)) {
                                    return NodeFilter.FILTER_REJECT;
                                }
                                parent = parent.parentElement;
                            }
                            return /(^|[^A-Za-z0-9_])@[A-Za-z0-9]/.test(node.nodeValue || '')
                                ? NodeFilter.FILTER_ACCEPT
                                : NodeFilter.FILTER_REJECT;
                        },
                    }
                );
                let node = walker.nextNode();
                while (node) {
                    textNodes.push(node);
                    node = walker.nextNode();
                }

                const mentionRegex = /(^|[^A-Za-z0-9_])@([A-Za-z0-9][A-Za-z0-9_.-]{0,48})/g;
                textNodes.forEach((textNode) => {
                    const text = textNode.nodeValue || '';
                    const fragment = document.createDocumentFragment();
                    let lastIndex = 0;
                    let match;
                    mentionRegex.lastIndex = 0;
                    while ((match = mentionRegex.exec(text)) !== null) {
                        const prefix = match[1] || '';
                        const handle = match[2] || '';
                        const tokenStart = match.index + prefix.length;
                        const token = `@${handle}`;
                        if (tokenStart > lastIndex) {
                            fragment.appendChild(document.createTextNode(text.slice(lastIndex, tokenStart)));
                        }
                        const span = document.createElement('span');
                        const normalizedHandle = handle.toLowerCase();
                        span.className = `collaboration-mention ${['lexius', 'abogado', 'ai'].includes(normalizedHandle) ? 'collaboration-mention--lexius' : 'collaboration-mention--human'}`;
                        span.textContent = token;
                        fragment.appendChild(span);
                        lastIndex = tokenStart + token.length;
                    }
                    if (lastIndex < text.length) {
                        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
                    }
                    if (fragment.childNodes.length) {
                        textNode.parentNode.replaceChild(fragment, textNode);
                    }
                });
                return wrapper.innerHTML;
            } catch (e) {
                return html;
            }
        },
        formatTimestamp(timestamp) {
            return new Date(timestamp).toLocaleString();
        },
        normalizeContextDocuments(rawDocs) {
            if (!Array.isArray(rawDocs)) return [];
            const normalized = [];
            const seen = new Set();

            rawDocs.forEach((item) => {
                if (!item || typeof item !== 'object') return;
                const path = String(item.path || '').trim();
                if (!path) return;
                const isUserDocument = item.is_user_document === undefined
                    ? path.includes('/get_user_upload')
                    : !!item.is_user_document;
                const key = `${isUserDocument ? 1 : 0}|${path}`;
                if (seen.has(key)) return;
                seen.add(key);
                normalized.push({
                    key,
                    path,
                    page_title: String(item.page_title || '').trim(),
                    is_user_document: isUserDocument,
                });
            });

            return normalized;
        },
        isSafeDocumentHref(path) {
            const value = String(path || '').trim();
            if (!value) return false;
            try {
                const parsed = new URL(value, window.location.origin);
                if (parsed.protocol && !['http:', 'https:'].includes(parsed.protocol)) {
                    return false;
                }
            } catch (e) {
                return false;
            }
            return !/^\s*(javascript|data|vbscript):/i.test(value);
        },
        documentHref(doc) {
            const path = String(doc?.path || '').trim();
            if (!this.isSafeDocumentHref(path)) return '';
            if (doc?.is_user_document && typeof buildUserUploadHref === 'function') {
                const hasCorporateScope = path.includes('scope=corporate');
                return buildUserUploadHref(
                    path,
                    doc.page_title || 'Documento',
                    hasCorporateScope ? 'corporate' : null,
                    hasCorporateScope ? '' : this.chatUuid
                );
            }
            return path;
        },
        replyToMessage() {
            if (!this.canReply) return;
            this.$emit('replyToMessage', this.message);
        },
        navigateToReply() {
            if (!this.replyPreview) return;
            this.$emit('navigateToMessage', this.replyPreview);
        },
        isUuidLike(value) {
            return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || '').trim());
        },
        friendlyNameFromEmail(email) {
            const normalizedEmail = String(email || '').trim();
            if (!normalizedEmail || !normalizedEmail.includes('@')) return '';
            const localPart = normalizedEmail.split('@')[0].split(/[._-]+/).filter(Boolean)[0] || normalizedEmail.split('@')[0];
            return localPart ? localPart.charAt(0).toUpperCase() + localPart.slice(1) : normalizedEmail;
        },
        avatarInitial(value, fallback = 'P') {
            const cleaned = String(value || '').trim().replace(/^@+/, '');
            const char = Array.from(cleaned)[0] || fallback || 'P';
            return String(char).toUpperCase();
        },
        reactionActor(item) {
            const displayName = String(item?.reactor_display_name || '').trim();
            const email = String(item?.reactor_email || '').trim();
            const displayNameIsEmail = displayName && email && displayName.toLowerCase() === email.toLowerCase();
            const displayNameIsUuid = displayName && this.isUuidLike(displayName);
            let label = '';
            if (displayName && !displayNameIsEmail && !displayNameIsUuid && !displayName.includes('@')) {
                label = displayName;
            } else if (email) {
                label = this.friendlyNameFromEmail(email) || email;
            } else {
                label = 'Participante';
            }
            const secondary = email && email.toLowerCase() !== label.toLowerCase() ? email : '';
            const initialsSource = (displayName && !displayNameIsUuid ? displayName : '') || email || label;
            const initials = initialsSource
                .split(/[\s@._-]+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part.charAt(0).toUpperCase())
                .join('') || 'P';
            return { label, secondary, initials };
        },
        toggleReactionDetails(reaction) {
            if (!reaction) return;
            this.activeReactionDetails = this.activeReactionDetails === reaction ? null : reaction;
        },
        closeReactionDetails() {
            this.activeReactionDetails = null;
        },
        handleReactionDetailsOutsideClick(event) {
            try {
                if (!this.activeReactionDetails) return;
                const target = event && event.target;
                if (
                    target
                    && this.$el
                    && this.$el.contains(target)
                    && typeof target.closest === 'function'
                    && target.closest('.collaboration-reactions')
                ) return;
                this.closeReactionDetails();
            } catch (e) { }
        },
        handleReactionDetailsKeydown(event) {
            if (event && event.key === 'Escape') {
                this.closeReactionDetails();
            }
        },
        react(reaction) {
            if (!['agree', 'disagree'].includes(reaction)) return;
            const payload = {
                message: this.message,
                reaction,
            };
            let parent = this.$parent;
            while (parent) {
                if (typeof parent.reactToMessage === 'function') {
                    parent.reactToMessage(payload);
                    return;
                }
                parent = parent.$parent;
            }
            this.$emit('reactToMessage', payload);
        },
        reactionLabel(reaction) {
            if (reaction === 'agree') return 'De acuerdo';
            if (reaction === 'disagree') return 'En desacuerdo';
            return reaction;
        },
        reactionDetailsTitle(group) {
            if (!group) return '';
            return `${this.reactionLabel(group.reaction)} (${group.count})`;
        },
        runQueuedAiRequest() {
            if (!this.canRunQueuedAi) return;
            this.$emit('runQueuedAiRequest', this.message);
        }
    },
    template: `
        <div class="flex gap-3 items-start !p-5 !pb-4">
            <div class="w-[40px] h-[40px] rounded-md overflow-hidden">
                <span
                    v-if="collaborative"
                    class="collaboration-message-avatar"
                    :title="senderPrimaryLabel || 'Participante'"
                    :aria-label="senderPrimaryLabel ? 'Mensaje de ' + senderPrimaryLabel : 'Mensaje de participante'"
                >
                    {{ senderAvatarInitial }}
                </span>
                <i v-else class="anticon anticon-user w-full h-full object-cover"/>
            </div>
            <div class="w-5/6 leading-7 paragraph-text dynamic-color">
                <template v-if="!editing">
                    <div v-if="senderPrimaryLabel || messageDateTime" class="collaboration-message-sender">
                        <span v-if="senderPrimaryLabel" class="collaboration-message-sender__identity">
                            <span class="collaboration-message-sender__name">{{ senderPrimaryLabel }}</span>
                            <span
                                v-if="senderSecondaryEmail"
                                class="collaboration-message-sender__email"
                            >
                                {{ senderSecondaryEmail }}
                            </span>
                        </span>
                        <span v-if="isAiQueued" class="collaboration-ai-queued">{{ aiQueueBadgeText }}</span>
                        <span v-else-if="message.human_only" class="collaboration-human-only">Grupo</span>
                        <span
                            v-if="messageDateTime"
                            class="collaboration-message-time"
                            data-testid="collaboration-message-time"
                        >{{ messageDateTime }}</span>
                    </div>
                    <button
                        v-if="replyPreview"
                        type="button"
                        class="collaboration-reply-reference"
                        data-testid="collaboration-reply-reference"
                        :title="'Ir al mensaje citado de ' + replyPreview.sender_label"
                        @click="navigateToReply"
                    >
                        <span class="collaboration-reply-reference__meta">{{ replyPreview.sender_label }}</span>
                        <span class="collaboration-reply-reference__snippet">{{ replyPreview.snippet }}</span>
                    </button>
                    <p v-html="formattedText"></p>
                    <div v-if="isAiQueued" class="collaboration-ai-queued-note" data-testid="collaboration-ai-queued-note">
                        <i class="anticon anticon-clock-circle" aria-hidden="true"></i>
                        <span>{{ aiQueueReason }}</span>
                        <button
                            v-if="canRunQueuedAi"
                            type="button"
                            class="collaboration-ai-queued-note__action"
                            @click="runQueuedAiRequest"
                        >
                            Enviar a Lexius
                        </button>
                    </div>
                    <div v-if="hasContextDocuments" class="user-message-documents">
                        <div class="user-message-documents__label">
                            <i class="anticon anticon-file-text"></i>
                            <span>{{ contextDocumentsLabel }}</span>
                        </div>
                        <div class="user-message-documents__list">
                            <a
                                v-for="doc in messageContextDocuments"
                                :key="doc.key"
                                :href="documentHref(doc)"
                                class="user-message-documents__chip"
                                :title="doc.path"
                            >
                                <span class="user-message-documents__chip-title">
                                    {{ doc.page_title || doc.path }}
                                </span>
                                <i class="anticon anticon-link user-message-documents__chip-icon"></i>
                            </a>
                        </div>
                    </div>
                    <div v-if="showButtons" class="mt-2 flex items-center gap-2">
                        <button
                            class="text-xs user-message-btn edit-btn"
                            :class="{ 'disabled': !canEdit, 'hover:text-theme-color': canEdit }"
                            :title="editButtonTitle"
                            @click="startEdit">
                            <i class="anticon anticon-edit"></i>
                        </button>
                        <button v-if="hasConversationHistory" class="text-xs hover:text-theme-color user-message-btn" title="Ver historial de conversación" @click="toggleConversationHistory">
                            <i class="anticon anticon-history"></i>
                            <span class="ml-1">Historial ({{ message.conversationHistory.length }})</span>
                        </button>
                    </div>
                    <div v-if="collaborative" class="collaboration-reactions">
                        <div class="collaboration-reactions__actions">
                            <button
                                v-if="canReply"
                                type="button"
                                title="Responder"
                                data-testid="collaboration-reply-user"
                                @click="replyToMessage"
                            >
                                <i class="anticon anticon-message"></i>
                            </button>
                            <button
                                type="button"
                                title="De acuerdo"
                                data-collaboration-reaction="agree"
                                :class="{ 'is-active': currentUserReactionMap.agree }"
                                :aria-pressed="currentUserReactionMap.agree ? 'true' : 'false'"
                                @click.stop.prevent="react('agree')"
                            >
                                <i class="anticon anticon-like"></i>
                            </button>
                            <button
                                type="button"
                                title="En desacuerdo"
                                data-collaboration-reaction="disagree"
                                :class="{ 'is-active': currentUserReactionMap.disagree }"
                                :aria-pressed="currentUserReactionMap.disagree ? 'true' : 'false'"
                                @click.stop.prevent="react('disagree')"
                            >
                                <i class="anticon anticon-dislike"></i>
                            </button>
                        </div>
                        <div v-if="groupedReactions.length" class="collaboration-reactions__summary">
                            <button
                                v-for="item in groupedReactions"
                                :key="item.reaction"
                                type="button"
                                class="collaboration-reactions__summary-chip"
                                :title="item.names.join(', ')"
                                :aria-expanded="activeReactionDetails === item.reaction ? 'true' : 'false'"
                                data-testid="collaboration-reaction-summary"
                                @click.stop="toggleReactionDetails(item.reaction)"
                            >
                                {{ reactionLabel(item.reaction) }} {{ item.count }}
                            </button>
                        </div>
                        <div
                            v-if="activeReactionGroup"
                            class="collaboration-reaction-details"
                            data-testid="collaboration-reaction-details"
                        >
                            <div class="collaboration-reaction-details__header">
                                <span>{{ reactionDetailsTitle(activeReactionGroup) }}</span>
                                <button type="button" title="Cerrar" @click="closeReactionDetails">
                                    <i class="anticon anticon-close"></i>
                                </button>
                            </div>
                            <div class="collaboration-reaction-details__list">
                                <div
                                    v-for="actor in activeReactionGroup.reactors"
                                    :key="actor.label + actor.secondary"
                                    class="collaboration-reaction-details__person"
                                >
                                    <span class="collaboration-reaction-details__avatar">{{ actor.initials }}</span>
                                    <span class="collaboration-reaction-details__identity">
                                        <span class="collaboration-reaction-details__name">{{ actor.label }}</span>
                                        <span v-if="actor.secondary" class="collaboration-reaction-details__meta">{{ actor.secondary }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Conversation History Section -->
                    <div v-if="hasConversationHistory && showConversationHistory" class="conversation-history mt-3">
                        <div class="conversation-history-header">
                            <div class="flex items-center justify-between">
                                <h6 class="text-sm font-medium text-secondary">Historial de Conversación</h6>
                                <button class="text-xs hover:text-theme-color" @click="toggleConversationHistory" title="Ocultar historial">
                                    <i class="anticon anticon-close"></i>
                                </button>
                            </div>
                            <p class="text-xs text-third mt-1">Versiones anteriores de este mensaje y sus respuestas</p>
                        </div>
                        
                        <div class="conversation-history-content">
                            <div v-for="(turn, index) in sortedConversationHistory" :key="turn.editedAt" class="conversation-turn">
                                <div class="turn-header">
                                    <span class="turn-number">Versión {{ sortedConversationHistory.length - index }}</span>
                                    <span class="turn-timestamp">{{ formatTimestamp(turn.editedAt) }}</span>
                                    <span v-if="turn.wasCancelled" class="turn-cancelled-badge">Cancelada</span>
                                </div>
                                
                                <div class="turn-content">
                                    <!-- Previous User Message -->
                                    <div class="previous-user-message">
                                        <div class="message-header">
                                            <i class="anticon anticon-user"></i>
                                            <span>Tu mensaje anterior:</span>
                                        </div>
                                        <div class="message-content" v-html="formatUserText(turn.userText)"></div>
                                    </div>
                                    
                                    <!-- Previous Bot Response -->
                                    <div v-if="turn.botResponse" class="previous-bot-response">
                                        <div class="message-header">
                                            <i class="anticon anticon-robot"></i>
                                            <span>Respuesta del Abogado Virtual:</span>
                                            <span v-if="turn.botResponse.cancelled" class="response-cancelled">(Cancelada)</span>
                                        </div>
                                        <div class="message-content bot-response" v-html="formatBotResponseText(turn.botResponse)"></div>
                                        
                                        <!-- Research Activity History -->
                                        <div v-if="turn.botResponse.researchActivityHistory && turn.botResponse.researchActivityHistory.length > 0" class="research-activity-summary">
                                            <div class="research-header">
                                                <i class="anticon anticon-experiment"></i>
                                                <span>Investigación realizada ({{ turn.botResponse.researchActivityHistory.length }} actividades)</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="no-bot-response">
                                        <div class="message-header">
                                            <i class="anticon anticon-robot"></i>
                                            <span>Sin respuesta del Abogado Virtual</span>
                                        </div>
                                        <p class="text-third text-sm">No se generó respuesta para esta versión del mensaje.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <form class="chat-composer-form chat-inline-editor" data-testid="inline-edit-composer" @submit.prevent="confirmEdit">
                        <div class="chat-composer-form__input">
                            <textarea
                                v-model="editText" ref="editInput" data-testid="inline-edit-input"
                                class="chat-input w-full border rounded p-2 text-sm" rows="3"
                                aria-label="Editar mensaje"
                                :aria-invalid="inlineTextTooLong ? 'true' : 'false'"
                                @beforeinput="pauseInlineDictationForEdit"
                                @compositionstart="pauseInlineDictationForEdit"
                                @input="onInlineInput"
                                @select="rememberInlineSelection"
                                @keyup="rememberInlineSelection"
                                @blur="rememberInlineSelection"
                            ></textarea>
                        </div>
                        <button
                            v-if="dictationEnabled" type="button" class="chat-dictation-button"
                            :class="{ 'is-recording': dictationState === 'listening' }"
                            data-testid="inline-edit-dictation-button"
                            :aria-label="dictationStarting ? 'Cancelar dictado' : (dictationBusy ? 'Detener dictado' : 'Dictar mensaje')"
                            :title="dictationStarting ? 'Cancelar dictado' : (dictationBusy ? 'Detener dictado' : 'Dictar mensaje')"
                            :aria-pressed="dictationState === 'listening' ? 'true' : 'false'"
                            :disabled="dictationState === 'finalizing' || (!dictationBusy && inlineDictationUnavailable)"
                            @click="toggleInlineDictation"
                        >
                            <svg v-if="dictationBusy" aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="5" width="14" height="14" rx="2" /></svg>
                            <svg v-else aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="9" y="2" width="6" height="13" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8"/></svg>
                        </button>
                        <button type="button" class="btn btn-sm chat-inline-edit-action" data-testid="inline-edit-cancel" @click="cancelEdit">Cancelar</button>
                        <button type="submit" class="btn btn-sm btn-primary chat-send chat-inline-edit-action" data-testid="inline-edit-confirm" :disabled="!inlineCanConfirm">Continuar</button>
                        <div v-if="inlineTextTooLong" class="chat-dictation-feedback" role="status">El mensaje es demasiado largo ({{ editText.length }} caracteres). Reduce su longitud a {{ inlineMaximumCharacters }} caracteres o menos.</div>
                        <div v-if="dictationEnabled" v-show="dictationStatusText || dictationRecovery" class="chat-dictation-feedback">
                            <div class="chat-dictation-status-row">
                                <span role="status" aria-live="polite" aria-atomic="true" data-testid="inline-edit-dictation-status">
                                    <span v-if="dictationState === 'listening'" class="chat-dictation-indicator" aria-hidden="true"></span>
                                    {{ dictationStatusText }}
                                </span>
                                <button v-if="dictationStarting" type="button" data-testid="inline-edit-dictation-cancel" @click="cancelInlineDictation">Cancelar dictado</button>
                                <button v-if="dictationState === 'error'" type="button" :disabled="inlineDictationUnavailable" @click="toggleInlineDictation">Reintentar</button>
                            </div>
                            <details v-if="dictationError && dictationError.help" class="chat-dictation-help">
                                <summary>Ayuda con el micrófono</summary>
                                <p>{{ dictationError.help }}</p>
                            </details>
                            <details v-if="dictationRecovery" class="chat-dictation-recovery" data-testid="inline-edit-dictation-recovery">
                                <summary>Revisar último fragmento</summary>
                                <p>{{ dictationRecovery }}</p>
                                <button type="button" @click="copyInlineRecovery">{{ dictationCopied ? 'Copiado' : 'Copiar fragmento' }}</button>
                            </details>
                        </div>
                    </form>
                    <div v-if="hasConversationHistory" class="mt-2 text-xs text-third">
                        <span>Historial disponible:</span>
                        <span class="ml-2">{{ message.conversationHistory.length }} versiones anteriores</span>
                    </div>
                </template>
            </div>
        </div>`
}
